<script>
import { mapState, mapActions, mapMutations } from "vuex";

import NavigationWrapper from "@/components/NavigationWrapper";
import MultiSelect from "@/components/MultiSelect";
import RolePermissionsTable from "@/pages/Team/Roles/Role/RolePermissionsTable";
import { defaultHandle } from "@/utils";

export default {
  // Included components
  components: {
    NavigationWrapper,
    MultiSelect,
    RolePermissionsTable,
  },

  data() {
    return {
      // The ID of the role being displayed
      roleId: null,

      // The actual role object
      role: null,

      // Selected groups
      selectedGroups: [],

      // Whether the role limits group access
      limitedGroupAccess: false,

      // The role's name
      name: "",

      // The role's handle
      handle: "",

      // The description of the role
      description: "",

      // Selected permissions
      selectedPermissions: {
        "team.manage": false,
        "roles.manage": false,
        "billing.manage": false,
        "members.view": false,
        "members.edit": false,
        "workers.view": false,
        "workers.edit": false,
        "groups.view": false,
        "groups.edit": false,
        "alerts.view": false,
        "alerts.edit": false,
      },
    };
  },

  computed: {
    // Whether or not we are creating a new role
    creating() {
      return this.roleId == null;
    },

    // List of selected permission IDs
    selectedPermissionIds() {
      return Object.values(this.permissions)
        .filter((permission) => {
          return this.selectedPermissions[
            permission.entity + "." + permission.action
          ];
        })
        .map((permission) => permission.id);
    },

    // List of selected group IDs
    selectedGroupIds() {
      return this.selectedGroups.map((group) => group.id);
    },

    // Mapped vuex state
    ...mapState(["roles", "groups", "permissions"]),
  },

  watch: {
    // When the name changes, update the handle to be the default value,
    // unless the handle has been manually changed to something different
    name(value, old) {
      if (this.handle === "" || this.handle === defaultHandle(old)) {
        this.handle = defaultHandle(value);
      }
    },
  },

  // Initialize the state of the component
  async created() {
    this.roleId = this.$route.params.roleId;
    this.role = this.roles[this.roleId];

    if (!this.creating) {
      // Get this current role
      await Promise.all([
        this.getRole({ roleId: this.roleId }),
        this.getPermissions(),
      ]);

      // If the role is read only, error and go back to roles
      if (this.role.read_only) {
        this.pushToast({
          title: "Error",
          message: `${this.role.name} role is read only`,
          variant: "danger",
        });
        await this.$router.replace("/team/roles/" + this.role.id);
        return;
      }

      // Set component data from role
      this.role = this.roles[this.roleId];
      this.name = this.role.name;
      this.handle = this.role.handle;
      this.description = this.role.description;
      this.limitedGroupAccess = this.role.limited_group_access;
      this.selectedGroups = this.role.accessible_groups.slice();

      Object.values(this.permissions).forEach((permission) => {
        this.selectedPermissions[
          permission.entity + "." + permission.action
        ] = false;
      });

      this.role.permissions.forEach((permission) => {
        this.selectedPermissions[
          permission.entity + "." + permission.action
        ] = true;
      });
    } else {
      // Get permissions from API
      await this.getPermissions();

      // Set default values for selected permissions
      Object.values(this.permissions).forEach((permission) => {
        this.selectedPermissions[
          permission.entity + "." + permission.action
        ] = false;
      });
    }
  },

  methods: {
    // On submit, either put or post the form data to the API,
    // depending on whether in edit or create mode
    async submit() {
      if (this.creating) {
        this.post();
      } else {
        this.put();
      }
    },

    // Update the role via the API
    async put() {
      // Update given the form inputs
      const success = await this.putRole({
        roleId: this.roleId,
        name: this.name,
        handle: this.handle,
        description: this.description,
        limitedGroupAccess: this.limitedGroupAccess,
        permissionIds: this.selectedPermissionIds,
        accessibleGroupIds: this.selectedGroupIds,
      });

      // If success, then redirect
      if (success) {
        await this.$router.replace("/team/roles/" + this.role.id);
        await this.getAccountRoles();
      }
    },

    // Post the new role via the API
    async post() {
      // Update given the form inputs
      let role = await this.postRole({
        name: this.name,
        handle: this.handle,
        description: this.description,
        limitedGroupAccess: this.limitedGroupAccess,
        permissionIds: this.selectedPermissionIds,
        accessibleGroupIds: this.selectedGroupIds,
      });

      // If success, then redirect
      if (role) {
        await this.$router.push("/team/roles/" + role.id);
        await this.getAccountRoles();
      }
    },

    // Determine whether a member matches a search term
    groupMatchesSearch(search, member) {
      search = search.trim().toLowerCase();
      let full_name = `${member.first_name} ${member.last_name}`;
      return (
        full_name.toLowerCase().includes(search) ||
        member.email.toLowerCase().includes(search) ||
        member.handle.toLowerCase().includes(search)
      );
    },

    // Helper methods and mapped vuex actions
    ...mapActions([
      "getRole",
      "getGroups",
      "getPermissions",
      "putRole",
      "postRole",
      "getAccountRoles",
    ]),
    ...mapMutations(["pushToast"]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/team">Settings</b-breadcrumb-item>
      <b-breadcrumb-item to="/team/roles">Roles</b-breadcrumb-item>
      <b-breadcrumb-item active v-if="creating">Add Role</b-breadcrumb-item>
      <b-breadcrumb-item :to="`/team/roles/${role.id}`" v-else>
        {{ role.name }}
      </b-breadcrumb-item>
      <b-breadcrumb-item active v-if="!creating">Edit</b-breadcrumb-item>
    </template>
    <b-row class="mb-2">
      <b-col cols="6">
        <h1>{{ !creating ? role.name : "Add Role" }}</h1>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-card class="Card-table mb-3">
          <div class="p-3">
            <b-form id="role-form" @submit.stop.prevent="submit">
              <b-row>
                <b-col>
                  <b-form-group id="name-group" label="Name" label-for="name">
                    <b-form-input
                      required
                      id="name"
                      v-model="name"
                      placeholder="Name"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="handle-group"
                    label="Handle"
                    label-for="handle"
                  >
                    <input
                      id="handle"
                      v-model="handle"
                      placeholder="Handle"
                      pattern="[a-z,0-9]([a-z,0-9]|(-[a-z,0-9]))*"
                      minlength="3"
                      maxlength="64"
                      autocomplete="off"
                      class="form-control"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group label="Description" label-for="description">
                <b-form-textarea
                  required
                  v-model="description"
                  id="description"
                  placeholder="Description of the role..."
                  rows="2"
                  no-resize
                ></b-form-textarea>
              </b-form-group>
              <div>
                <b-form-group>
                  <b-form-checkbox v-model="limitedGroupAccess">
                    Limited Group Access
                  </b-form-checkbox>
                  <small class="form-text text-muted">
                    Users with limited group access with only be able to see
                    specific groups, and cannot create groups or workers.
                  </small>
                </b-form-group>
                <b-form-group
                  v-if="limitedGroupAccess"
                  id="destination-worker-name-group"
                  :label="`Accessible Groups`"
                  label-for="groups"
                >
                  <MultiSelect
                    primary-key="id"
                    v-model="selectedGroups"
                    :options="groups"
                    :match="groupMatchesSearch"
                    size="sm"
                  ></MultiSelect>
                  <small class="form-text text-muted">
                    Groups that users with this role have access to.
                  </small>
                </b-form-group>
              </div>
            </b-form>
          </div>
          <RolePermissionsTable v-model="selectedPermissions" />

          <div class="p-3">
            <b-button type="submit" form="role-form" variant="primary">
              {{ creating ? "Add" : "Update" }} Role
            </b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </NavigationWrapper>
</template>

<style scoped></style>
